import { useState } from "react";
import { NavLink, Link, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, PenSquare, Briefcase, LayoutGrid, Video, Award, Star, HelpCircle,
  Inbox, Image as ImageIcon, Search, BarChart3, Settings, ExternalLink, LogOut, Menu, X, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import Logo from "@/components/Logo";
import { AnimatePresence, motion } from "framer-motion";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/content", label: "Content Editor", icon: PenSquare, testid: "admin-nav-content" },
  { to: "/admin/services", label: "Services", icon: Briefcase, testid: "admin-nav-services" },
  { to: "/admin/samples", label: "Samples", icon: LayoutGrid, testid: "admin-nav-samples" },
  { to: "/admin/ugc", label: "UGC Models", icon: Video, testid: "admin-nav-ugc" },
  { to: "/admin/proofs", label: "Proofs", icon: Award, testid: "admin-nav-proofs" },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star, testid: "admin-nav-testimonials" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, testid: "admin-nav-faqs" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "admin-nav-leads" },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon, testid: "admin-nav-media" },
  { to: "/admin/seo", label: "SEO Manager", icon: Search, testid: "admin-nav-seo" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "admin-nav-analytics" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#0B0B14] flex items-center justify-center" data-testid="admin-auth-loading">
        <Loader2 className="h-8 w-8 text-accent-bright animate-spin" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const brand = settings?.brand || {};

  const sidebar = (
    <div className="h-full flex flex-col bg-[#0B0B14]/95 border-r border-white/[0.07]">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-white/[0.07] shrink-0">
        <Logo settings={settings} size={32} />
        <div>
          <p className="font-display font-bold text-sm text-white leading-tight">{brand.short_name || "Scalovix"}</p>
          <p className="font-mono-label text-[8px] text-accent-bright">Admin Panel</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive ? "bg-accent/15 text-white border border-accent/25" : "text-mist hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`
            }
            data-testid={n.testid}
          >
            <n.icon className="h-4 w-4 shrink-0" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/[0.07] space-y-1 shrink-0">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-mist hover:text-white hover:bg-white/[0.04] transition-colors" data-testid="admin-view-site-link">
          <ExternalLink className="h-4 w-4" /> View Live Site
        </Link>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-rose-400/90 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" data-testid="admin-logout-btn">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0B14]" data-testid="admin-layout">
      <div className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30">{sidebar}</div>
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            <motion.div initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ duration: 0.25 }} className="fixed inset-y-0 left-0 w-64 z-50 lg:hidden">
              {sidebar}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 backdrop-blur-xl bg-[#0B0B14]/85 border-b border-white/[0.07] flex items-center justify-between px-4 sm:px-6">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-white" data-testid="admin-mobile-menu-toggle" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden lg:flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 node-pulse" />
            <span className="font-mono-label text-[10px] text-subtle">Content changes go live instantly</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-subtle hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <span className="h-8 w-8 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center text-xs font-bold text-accent-bright">
              {(user.name || "A")[0]}
            </span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
